import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAdminAuth } from "./AuthContext";
import { Toaster } from "../components/ui/sonner";
import {
  House, Article, Briefcase, Users, FolderSimple, Sliders,
  Note, EnvelopeOpen, ChartLine, SignOut, ArrowSquareOut, ImageSquare, Storefront, SquaresFour, UsersFour, Buildings, FileText,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/admin",              label: "Dashboard",         icon: House,         end: true },
  { to: "/admin/home-sections",label: "Home Sections",     icon: SquaresFour },
  { to: "/admin/slider",       label: "Hero Slider",       icon: ImageSquare },
  { to: "/admin/brands",       label: "Products / Brands", icon: Storefront },
  { to: "/admin/core-team",    label: "Core Team",         icon: UsersFour },
  { to: "/admin/client-logos", label: "Client Logos",      icon: Buildings },
  { to: "/admin/client-projects", label: "Clients Portfolio", icon: FolderSimple },
  { to: "/admin/blog",         label: "Blog Posts",        icon: Article },
  { to: "/admin/team",         label: "Team (legacy)",     icon: Users },
  { to: "/admin/jobs",         label: "Job Openings",      icon: Briefcase },
  { to: "/admin/content",      label: "Page Content",      icon: Note },
  { to: "/admin/detail-pages", label: "Detail Pages",      icon: FileText },
  { to: "/admin/settings",     label: "Site Settings",     icon: Sliders },
  { to: "/admin/leads",        label: "Leads",             icon: EnvelopeOpen },
  { to: "/admin/analytics",    label: "Analytics",         icon: ChartLine },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAdminAuth();
  const nav = useNavigate();

  React.useEffect(() => {
    if (!loading && !user) nav("/admin/login", { replace: true });
  }, [user, loading, nav]);

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center text-[#6B7A8D]">Loading…</div>;
  }

  return (
    <div className="min-h-screen flex" style={{ background: "#F5F7FA" }}>
      <aside className="w-[240px] shrink-0 bg-white border-r border-slate-200 flex flex-col" data-testid="admin-sidebar">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2 leading-none">
          <img src="/logo.png" alt="OpSysDigital" className="h-9 w-auto" />
          <span className="ml-auto text-[9.5px] uppercase tracking-[0.18em] text-[#6B7A8D]">CMS</span>
        </div>
        <nav className="px-3 py-4 flex-1 overflow-y-auto">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2 rounded-md text-[13.5px] font-medium mb-0.5 transition ${
                    isActive
                      ? "bg-[#0F2A6E] text-white"
                      : "text-[#1C2B3A] hover:bg-slate-100"
                  }`
                }
              >
                <Icon size={16} weight="duotone" /> {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-slate-100 p-3">
          <a href="/" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 rounded-md text-[13px] text-[#1C2B3A] hover:bg-slate-100">
            <ArrowSquareOut size={14} weight="bold" /> View site
          </a>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="w-full mt-1 flex items-center gap-2 px-3 py-2 rounded-md text-[13px] text-[#1B6FE0] hover:bg-blue-50"
          >
            <SignOut size={14} weight="bold" /> Sign out
          </button>
          <div className="mt-3 px-3 text-[11px] text-[#6B7A8D] truncate">
            {user.email}
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <Outlet />
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
}
